import { useState, useCallback } from "react";
import type {
  CategoryViewModel,
  CreateCategoryPayload,
  UpdateCategoryPayload,
} from "@models/category";
import { categoryApi } from "@src/services/categoryApi";

export interface ICategoryState {
  categories: CategoryViewModel[];
  isLoading: boolean;
  error: string | null;
  fetchCategories: () => Promise<void>;
  createCategory: (data: CreateCategoryPayload) => Promise<CategoryViewModel>;
  updateCategory: (
    categoryId: string,
    data: UpdateCategoryPayload,
  ) => Promise<CategoryViewModel>;
  deleteCategory: (categoryId: string) => Promise<void>;
}

export const useCategory = (): ICategoryState => {
  const [categories, setCategories] = useState<CategoryViewModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await categoryApi.getCategories();
      setCategories(response);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch categories";
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createCategory = useCallback(
    async (data: CreateCategoryPayload): Promise<CategoryViewModel> => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await categoryApi.createCategory(data);
        await fetchCategories();
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create category";
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchCategories],
  );

  const updateCategory = useCallback(
    async (
      categoryId: string,
      data: UpdateCategoryPayload,
    ): Promise<CategoryViewModel> => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await categoryApi.updateCategory(categoryId, data);
        await fetchCategories();
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update category";
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchCategories],
  );

  const deleteCategory = useCallback(
    async (categoryId: string): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);
        await categoryApi.deleteCategory(categoryId);
        await fetchCategories();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete category";
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchCategories],
  );

  return {
    categories,
    isLoading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
  };
};
